import { useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import DramaCard from "@/components/base/DramaCard";
import { useSeo } from "@/hooks/useSeo";
import { dramas, dramaCategories } from "@/mocks/dramas";

type SortKey = "heat" | "rating" | "new";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "heat", label: "按热度" },
  { key: "rating", label: "按口碑分" },
  { key: "new", label: "按上线时间" },
];

export default function DramasPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") || "all";
  const query = searchParams.get("q") || "";
  const [sortKey, setSortKey] = useState<SortKey>("heat");
  const [keyword, setKeyword] = useState(query);

  useSeo({
    title: "短剧库 — 爆料短剧全集分类与演员表查询",
    description:
      "按都市、古装、玄幻、悬疑等题材浏览爆料短剧，支持剧名与演员关键词搜索，快速进入详情页查看演员表、导演编剧信息与拍摄花絮。",
    keywords: "爆料短剧,短剧库,演员表查询,导演编剧信息,短剧分类,剧集口碑评分",
    path: "/dramas",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "短剧库",
      url: "https://beesfly.cn/dramas",
      description: "按题材与关键词浏览爆料短剧的分类页面。",
    },
  });

  const filtered = useMemo(() => {
    const list = dramas.filter((drama) => {
      const matchCat = activeCat === "all" || drama.tags.includes(activeCat);
      const text = `${drama.title}${drama.director}${drama.screenwriter}${drama.cast
        .map((item) => item.name)
        .join("")}`;
      const matchQuery = !query || text.includes(query);
      return matchCat && matchQuery;
    });

    return list.sort((a, b) => {
      if (sortKey === "rating") return b.rating - a.rating;
      if (sortKey === "new") return b.year - a.year || b.heat - a.heat;
      return b.heat - a.heat;
    });
  }, [activeCat, query, sortKey]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    updateParam("q", keyword.trim());
  };

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "短剧库" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
            短剧库
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            收录 {dramas.length} 部正在被讨论的爆料短剧。按题材筛选或直接搜索剧名与演员姓名，
            即可进入详情页查看完整的演员表、导演编剧信息与拍摄花絮。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-wrap gap-2">
            {dramaCategories.map((cat) => {
              const isActive = activeCat === cat.key;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => updateParam("cat", cat.key === "all" ? "" : cat.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <form onSubmit={submitSearch} className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2">
              <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索剧名 / 演员 / 导演"
                aria-label="搜索短剧"
                className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400 sm:w-60"
              />
              <button type="submit" className="whitespace-nowrap text-sm text-primary-600 hover:text-primary-700">
                搜索
              </button>
            </form>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">排序</span>
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSortKey(option.key)}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                    sortKey === option.key
                      ? "bg-accent-100 text-accent-900"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {query && (
          <p className="mt-6 text-sm text-foreground-600">
            搜索「<span className="text-foreground-950">{query}</span>」共找到 {filtered.length} 部作品
          </p>
        )}

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {filtered.map((drama) => (
              <DramaCard key={drama.id} drama={drama} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 font-heading text-base font-semibold text-foreground-950">
              没有找到符合条件的短剧
            </p>
            <p className="mt-2 text-sm text-foreground-600">换个关键词，或点击下方按钮重置筛选条件。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setSearchParams({}, { replace: true });
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}